import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../Context/LanguageContext';
import PageTransition from './PageTransition';
import { useJobData } from '../hooks/useJobData';

const isValidUrl = (url) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const IsIlanlari = () => {
  const { language, t } = useLanguage();
  const { jobs, loading } = useJobData(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  // Helper to get localized field
  const loc = (job, field) => {
    if (language === 'tr') return job[field] || job[field + 'En'];
    return job[field + 'En'] || job[field];
  };

  // Derive unique countries from data
  const countries = useMemo(() => {
    const set = new Map();
    jobs.forEach(job => {
      const key = job.country; // use TR as canonical key
      if (!set.has(key)) {
        set.set(key, { tr: job.country, en: job.countryEn });
      }
    });
    const sorted = Array.from(set.values()).sort((a, b) => {
      const nameA = language === 'tr' ? a.tr : a.en;
      const nameB = language === 'tr' ? b.tr : b.en;
      return nameA.localeCompare(nameB, language === 'tr' ? 'tr' : 'en');
    });
    return sorted;
  }, [language, jobs]);

  // Derive cities filtered by selected country
  const cities = useMemo(() => {
    const set = new Map();
    jobs
      .filter(job => !selectedCountry || job.country === selectedCountry)
      .forEach(job => {
        const key = job.city; // use TR as canonical key
        if (!set.has(key)) {
          set.set(key, { tr: job.city, en: job.cityEn });
        }
      });
    const sorted = Array.from(set.values()).sort((a, b) => {
      const nameA = language === 'tr' ? a.tr : a.en;
      const nameB = language === 'tr' ? b.tr : b.en;
      return nameA.localeCompare(nameB, language === 'tr' ? 'tr' : 'en');
    });
    return sorted;
  }, [language, selectedCountry, jobs]);

  // Reset city when country changes and city no longer valid
  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    // Check if current city is still valid under new country
    if (value) {
      const validCities = jobs
        .filter(job => job.country === value)
        .map(job => job.city);
      if (selectedCity && !validCities.includes(selectedCity)) {
        setSelectedCity('');
      }
    }
  };

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Country filter
      if (selectedCountry && job.country !== selectedCountry) return false;

      // City filter
      if (selectedCity && job.city !== selectedCity) return false;

      // Text search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const title = (language === 'tr' ? job.titleTr : job.titleEn).toLowerCase();
        const company = job.company.toLowerCase();
        const city = (language === 'tr' ? job.city : job.cityEn).toLowerCase();
        const country = (language === 'tr' ? job.country : job.countryEn).toLowerCase();
        if (
          !title.includes(q) &&
          !company.includes(q) &&
          !city.includes(q) &&
          !country.includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [jobs, searchQuery, selectedCountry, selectedCity, language]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCountry('');
    setSelectedCity('');
  };

  const hasActiveFilters = searchQuery || selectedCountry || selectedCity;

  return (
    <PageTransition>
    <section id="isilanlari" className="jobs-section">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2>{t('jobs.title')}</h2>
          <p className="section-subtitle">{t('jobs.subtitle')}</p>
        </motion.div>

        {loading ? (
          <motion.div
            className="jobs-loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: 'center', padding: '60px 20px' }}
          >
            <i className="fa fa-spinner fa-spin" aria-hidden="true" style={{ fontSize: '2rem', color: 'var(--primary-color)', marginBottom: '16px', display: 'block' }}></i>
            <p style={{ color: '#666' }}>{language === 'tr' ? 'İlanlar yükleniyor...' : 'Loading job listings...'}</p>
          </motion.div>
        ) : (
        <>
        {/* Search & Filters */}
        <div className="jobs-filters-wrapper">
          {/* Search */}
          <div className="jobs-search-box">
            <i className="fa fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder={language === 'tr' ? 'Pozisyon, firma veya konum ara...' : 'Search by title, company or location...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="jobs-search-clear" onClick={() => setSearchQuery('')}>
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            )}
          </div>

          {/* Dropdowns row */}
          <div className="jobs-dropdowns">
            <div className="jobs-select-wrapper">
              <i className="fa fa-globe" aria-hidden="true"></i>
              <select
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
              >
                <option value="">{language === 'tr' ? 'Tüm Ülkeler' : 'All Countries'}</option>
                {countries.map(c => (
                  <option key={c.tr} value={c.tr}>
                    {language === 'tr' ? c.tr : c.en}
                  </option>
                ))}
              </select>
            </div>

            <div className="jobs-select-wrapper">
              <i className="fa fa-map-marker" aria-hidden="true"></i>
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
              >
                <option value="">{language === 'tr' ? 'Tüm Şehirler' : 'All Cities'}</option>
                {cities.map(c => (
                  <option key={c.tr} value={c.tr}>
                    {language === 'tr' ? c.tr : c.en}
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <button className="jobs-clear-filters" onClick={clearAllFilters}>
                <i className="fa fa-times" aria-hidden="true"></i>
                {language === 'tr' ? 'Temizle' : 'Clear'}
              </button>
            )}
          </div>
        </div>

        {/* Results count */}
        <p className="jobs-count">
          {filteredJobs.length} {language === 'tr' ? 'ilan bulundu' : (filteredJobs.length === 1 ? 'job found' : 'jobs found')}
        </p>

        {/* Job cards grid */}
        <AnimatePresence mode="wait">
          {filteredJobs.length > 0 ? (
            <motion.div
              className="jobs-grid"
              key={'jobs-grid-' + selectedCountry + selectedCity + searchQuery}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  className="job-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06, duration: 0.35 }}
                >
                  <div className="job-card-body">
                    <h3 className="job-title">
                      {language === 'tr' ? job.titleTr : job.titleEn}
                    </h3>
                    <div className="job-meta">
                      <span className="job-company">
                        <i className="fa fa-building-o" aria-hidden="true"></i>
                        {job.company}
                      </span>
                      <span className="job-location">
                        <i className="fa fa-map-marker" aria-hidden="true"></i>
                        {loc(job, 'city')}, {loc(job, 'country')}
                      </span>
                    </div>
                  </div>
                  {isValidUrl(job.link) && (
                  <a
                    href={job.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="job-apply-btn"
                  >
                    {t('jobs.apply')}
                    <i className="fa fa-arrow-right" aria-hidden="true"></i>
                  </a>
                  )}
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              className="jobs-empty"
              key="jobs-empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
            >
              <i className="fa fa-briefcase" aria-hidden="true"></i>
              <h3>{t('jobs.noJobs')}</h3>
              <p>{language === 'tr' ? 'Arama kriterlerinize uygun ilan bulunamadı.' : 'No listings match your search criteria.'}</p>
            </motion.div>
          )}
        </AnimatePresence>
        </>
        )}

        {/* Job Submission CTA */}
        <motion.div
          className="job-submit-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="job-submit-cta-content">
            <i className="fa fa-plus-circle job-submit-cta-icon" aria-hidden="true"></i>
            <div className="job-submit-cta-text">
              <h3>{t('jobs.submitTitle')}</h3>
              <p>{t('jobs.submitDesc')}</p>
            </div>
          </div>
          <a
            href="https://forms.gle/sXEzNg6PdMzFNDoz6"
            target="_blank"
            rel="noopener noreferrer"
            className="job-submit-btn"
          >
            {t('jobs.submitBtn')}
            <i className="fa fa-external-link" aria-hidden="true"></i>
          </a>
        </motion.div>
      </div>
    </section>
    </PageTransition>
  );
};

export default IsIlanlari;
